/**
 * What a report's periods open, the browser half of PHP's CellActions: a scorecard grid cell's
 * buttons (superset-table.js, through superset-table-grid.js), which open its source's own record
 * through its deeplink and its drilldown, and a metric card's expand icon and chart clicks
 * (report-page.js), which open its drilldown. The drilldown is DrilldownTable's modal
 * (livewire/drilldown-table.blade.php, rendered once per page), opened by the
 * `show-drilldown-modal` event.
 *
 * When the modal closes, focus goes back to the control that opened it (returnFocusAfterDrilldown(),
 * and returnFocusAfterModal() for any other x-modal, a report card's type editor's).
 * The modal's x-trap returns focus to whatever held it when the modal opened: the pressed button
 * only where a click focuses a button (not in Safari), and after a click on a report card's chart
 * that chart's figure, which the click focused (it is focusable, tabindex -1), so focus is back
 * where the reader clicked. And the modal is hidden before it goes: its @if drops it a server round
 * trip later, and until the browser's next style update its own close button, hidden, still holds
 * focus. So once the modal has opened and hidden, this waits a frame and then past x-trap's own
 * return (a timeout), and moves focus to the control if it landed nowhere: <body>, a node that left
 * the page, or anything inside the modal. Focus anywhere else (that figure, or wherever the reader
 * put it) is left alone.
 *
 * Imports nothing, so app.js can reach it through the table host on every page, and the report
 * page's Node harness can import it.
 */

/** The event DrilldownTable::openDrilldown() listens for; its detail's keys are its arguments. */
export const DRILLDOWN_EVENT = 'show-drilldown-modal';

/**
 * A deeplink the page may open, as an absolute URL, or null. Only http and https: a deeplink is
 * stored per metric and templated per source, so anything else (a javascript: URL most of all) is
 * refused here rather than handed to window.open or an href.
 */
export function safeDeeplink(href, base = globalThis.location?.href) {
    if (typeof href !== 'string' || href === '') return null;

    try {
        const url = new URL(href, base);

        return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
    } catch {
        return null;
    }
}

/** DrilldownTable's modal, which the page renders once. */
const DRILLDOWN_MODAL_ID = 'drilldown-table-modal';

/** How long a pressed control waits for its modal before it stops watching for one. */
const MODAL_OPEN_TIMEOUT_MS = 15000;

/** An x-modal's root carries its id and is x-show'n: display none while closed, and gone once an @if drops it. */
function isModalOpen(modalId) {
    const modal = document.getElementById(modalId);

    return Boolean(modal) && modal.style.display !== 'none';
}

/** Focus is on nothing a reader chose: <body>, a node that left the page, or the closed modal. */
function focusLandedNowhere(modalId) {
    const active = document.activeElement;

    return ! active
        || active === document.body
        || ! active.isConnected
        || Boolean(document.getElementById(modalId)?.contains(active));
}

/**
 * Watch for the modal `modalId` about to open to open and close, then focus `findTarget()`'s element
 * if focus landed nowhere. The target is looked up when the modal closes, not now, so a control
 * drawn again meanwhile is still found. Returns a function that stops watching; the watch also stops
 * itself when no modal opens within MODAL_OPEN_TIMEOUT_MS.
 *
 * @param {string} modalId
 * @param {() => ?HTMLElement} findTarget
 * @returns {() => void}
 */
export function returnFocusAfterModal(modalId, findTarget) {
    if (typeof MutationObserver === 'undefined') return () => {};

    let opened = false;
    let timer = null;
    const observer = new MutationObserver(() => {
        if (! opened) {
            opened = isModalOpen(modalId);
            return;
        }
        if (isModalOpen(modalId)) return;

        stop();
        requestAnimationFrame(() => setTimeout(() => {
            if (! focusLandedNowhere(modalId)) return;

            findTarget()?.focus();
        }, 0));
    });
    const stop = () => {
        observer.disconnect();
        clearTimeout(timer);
    };

    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    timer = setTimeout(() => {
        if (! opened) stop();
    }, MODAL_OPEN_TIMEOUT_MS);

    return stop;
}

/** returnFocusAfterModal() for DrilldownTable's modal, which every drilldown opens. */
export function returnFocusAfterDrilldown(findTarget) {
    return returnFocusAfterModal(DRILLDOWN_MODAL_ID, findTarget);
}
